/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, ReactElement } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import moment from "moment";
import { Badge } from "@chakra-ui/react";
import axios from "axios";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineDoNotDisturbAlt } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import UserUpdateStatusConfirmModal from "../../shared/UserUpdateStatusConfirmModal";

type User = {
  user_id: string;
  username: string;
  email: string;
  role: string;
  status: string;
  merchant: any;
  agent: any;
  created_date: string;
  latest_login: string;
};

interface UserStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

interface Filters {
  [key: string]: any;
}

const initialFilters: Filters = {
  createdDateRange: [null, null],
};

const AgentUsersTab: React.FC<{
  currentUsername: string;
  agentId: string;
}> = ({ currentUsername, agentId }) => {
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(10);
  const [agents, setAgents] = useState([]);
  const [swalProps, setSwalProps] = useState<any>({});
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const [isOpenUpdateStatusConfirmModal, setIsOpenUpdateStatusConfirmModal] =
    useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<string>("");
  const [userDetailToUpdate, setUserDetailToUpdate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (currentUsername) {
        fetchUsers();
      }

      const fetchedAgents = await getAgentOptions();
      setAgents(fetchedAgents);
    };

    fetchData();
  }, [page, pageSize, currentUsername]); // eslint-disable-line

  const fetchUsers = async () => {
    try {
      setIsFetchingUsers(true);
      const { createdDateRange } = initialFilters;

      const startDate = createdDateRange[0]?.toISOString() || "";
      const endDate = createdDateRange[1]?.toISOString() || "";

      const params = {
        agent_id: agentId,
        startDate,
        endDate,
        page: (page + 1).toString(),
        pageSize: pageSize.toString(),
        currentUsername,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/get-all-users`,
        { params }
      );
      let { data, totalCount } = response.data;

      setTableData(data);
      setRowCount(totalCount);

      setIsFetchingUsers(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setTableData([]);
      setIsFetchingUsers(false);
      setRowCount(0);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setPage(newValue.page);
    setPageSize(newValue.pageSize);
  };

  const getAgentOptions = async () => {
    localStorage.removeItem("agentName");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/datasource/agents`
      );
      if (response.data.agents && response.data.agents.payload) {
        return [
          { id: "", name: "-- Please Select --" },
          ...response.data.agents.payload,
        ];
      } else {
        console.error("Invalid response format for get-all-agents");
        return [{ id: "", name: "-- Please Select --" }];
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const statusClasses = "status";

  const statusList: UserStatusItems[] = [
    {
      status: "Inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "Active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
  ];

  const formatDate = (dateString: string) => {
    return moment(dateString, "YYYY-MM-DD HH:mm:ss").format(
      "DD MMM YY - hh:mm A"
    );
  };

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<User, string>) => {
        const isCurrentUser = params.row.username === currentUsername;
        return (
          <span className={isCurrentUser ? "font-bold" : ""}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<User, string>) => {
        const isCurrentUser = params.row.username === currentUsername;
        return (
          <span className={isCurrentUser ? "font-bold" : ""}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "latest_login",
      headerName: "Latest Login",
      flex: 1,
      minWidth: 220,
      sortable: false,
      valueFormatter: (params) => {
        const dateString = params.value as string;
        if (!dateString) return "-";
        return formatDate(dateString);
      },
    },
    {
      field: "status",
      headerName: "",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<User, string>) => {
        const status = params.value as string;
        const statusItem = statusList.find((item) => item.status === status);
        return statusItem ? statusItem.display : null;
      },
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1,
      minWidth: 220,
      sortable: false,
      headerAlign: "right",
      align: "right",
      renderCell: (params: GridRenderCellParams<User, string>) => (
        <div className="flex items-center">
          {/* {canEdit(currentUserRole, params.row.role) && ( */}
          <>
            <Tooltip title="View More Details" arrow placement="top">
              <IconButton
                disabled={isFetchingUsers}
                id={"button-detail-" + params?.row?.user_id}
                onClick={() => handleViewDetail(params?.row?.user_id)}
              >
                <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={params.row.status === "Active" ? "Deactivate" : "Activate"}
              arrow
              placement="top"
            >
              <IconButton
                disabled={isFetchingUsers}
                id={"button-change-status-" + params?.row?.user_id}
                onClick={() =>
                  handleUpdateStatus(
                    params.row.status === "Active" ? "inactive" : "active",
                    params?.row?.user_id
                  )
                }
              >
                {params.row.status === "Active" ? (
                  <MdOutlineDoNotDisturbAlt className="h-5 w-5 text-red-500" />
                ) : (
                  <AiOutlineCheckCircle className="h-5 w-5 text-primary-500" />
                )}
              </IconButton>
            </Tooltip>
          </>
          {/* )} */}
        </div>
      ),
    },
  ];

  const handleViewDetail = (userId: any) => {
    const user = tableData.filter((x: any) => x.user_id === userId)[0];
    if (user.agentId) {
      const agentId = user.agentId;
      const agent = agents.filter((x: any) => x.id === agentId)[0];
      localStorage.setItem("agentName", agent.name);
    }
    navigate(`/${userRole}/user-management/${userId}`);
  };

  const handleUpdateStatus = (statusToUpdate: string, user_id: any) => {
    const user = tableData.filter((x: any) => x.user_id === user_id)[0];
    setUserDetailToUpdate(user);
    setStatusToUpdate(statusToUpdate);
    showUpdateStatusConfirmModal();
  };

  const showUpdateStatusConfirmModal = () => {
    setIsOpenUpdateStatusConfirmModal(true);
  };

  const closeUpdateStatusConfirmModal = () => {
    setIsOpenUpdateStatusConfirmModal(false);
  };

  const triggerReloadList = async () => {
    setUserDetailToUpdate(null);
    setStatusToUpdate("");
    setIsFetchingUsers(true);
    await fetchUsers();
  };

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start text-md",
    row: "",
  };

  return (
    <>
      {isOpenUpdateStatusConfirmModal && userDetailToUpdate && (
        <UserUpdateStatusConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenUpdateStatusConfirmModal}
          onClose={closeUpdateStatusConfirmModal}
          userToUpdate={userDetailToUpdate}
          statusToUpdate={statusToUpdate}
        />
      )}
      <SweetAlert2 {...swalProps} />
      <div className="px-4 py-8">
        <Box>
          <DataGrid
            sx={{
              "& .MuiDataGrid-cell": {
                padding: "8px 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "8px 16px",
              },
              "& .MuiTablePagination-input": {
                marginRight: "-20px",
              },
              "& .MuiTablePagination-displayedRows": {
                display: "none",
              },
            }}
            loading={isFetchingUsers}
            rows={tableData || []}
            columns={columns}
            getRowId={(row) => (row ? row.email : undefined)}
            rowCount={rowCount}
            sortingMode="server"
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            paginationModel={{ page: page, pageSize: pageSize }}
            onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            getCellClassName={(params) => {
              if (params.row.username === currentUsername) {
                return "text-primary-500";
              }
              return "";
            }}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      </div>
    </>
  );
};

export default AgentUsersTab;
